import React, { useState, useEffect } from 'react'
import {
    Button,
    Typography,
    Box,
    Divider,
    Chip,
    CircularProgress,
    Paper,
    Grid,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Pagination,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import IconButton from '@mui/material/IconButton'
import { FormattedMessage, injectIntl } from 'react-intl'
import moment from 'moment'
import { makeStyles, useTheme } from '@mui/styles'
import { getFileByPath } from 'src/services/attachment.service'
import { getPaginateByLlmDocumentId } from 'src/services/llmdocumentstatushistory.service'
import { PapiroConsole } from 'src/utils/papiroConsole'
import { Timeline } from '@mui/icons-material'
import { LLMDocumentStatusHistoryTimeline } from 'src/components/details-dialogs/llm-document-status-history-timeline'

// Detecta o tema atual baseado nas variáveis CSS
const detectTheme = () => {
    const primaryColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--mexx-green-primary')
        .trim()

    const isBlueTheme = primaryColor.includes('2979ff') || primaryColor.includes('#2979ff')
    return isBlueTheme ? 'blue' : 'green'
}

// Cores do tema para campos personalizados
const getThemeColors = (themeName) => {
    if (themeName === 'blue') {
        return {
            primary: '#1E88E5',
            light: '#E3F2FD',
            dark: '#1565C0',
            border: '#BBDEFB',
            accent: '#0D47A1',
        }
    } else {
        return {
            primary: '#558B2F',
            light: '#F1F8E9',
            dark: '#33691E',
            border: '#DCEDC8',
            accent: '#1B5E20',
        }
    }
}

const useStyles = makeStyles((theme) => ({
    panelContainer: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    panelContent: {
        padding: theme.spacing(2.5),
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'block',
        minHeight: 0,
        backgroundColor: '#FFFFFF',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
        },
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#CFD8DC',
            borderRadius: '3px',
            '&:hover': {
                backgroundColor: '#B0BEC5',
            }
        }
    },
    infoSection: {
        marginBottom: theme.spacing(2),
    },
    label: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
        color: '#546E7A',
        fontSize: '0.8rem',
    },
    value: {
        marginBottom: theme.spacing(1),
        wordBreak: 'break-word',
        color: '#37474F',
        fontSize: '0.875rem',
    },
    colorIndicator: {
        width: 12,
        height: 12,
        borderRadius: '50%',
        display: 'inline-block',
        marginRight: '6px',
    },
    descriptionBox: {
        padding: theme.spacing(2),
        backgroundColor: '#FAFAFA',
        borderRadius: '8px',
        marginTop: theme.spacing(1),
        border: '1px solid #ECEFF1',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '4px',
        },
        '& a': {
            wordBreak: 'break-all',
        },
        '& pre, & code': {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
        },
    },
    timestamp: {
        fontSize: '0.6875rem',
        color: '#718096',
        fontWeight: 500,
    },
    infoBar: {
        padding: theme.spacing(2, 3),
        backgroundColor: 'white',
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    infoLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#718096',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: theme.spacing(0.5),
    },
    infoValue: {
        fontSize: '0.875rem',
        color: '#2D3748',
        fontWeight: 500,
    },
    attachmentCard: {
        marginTop: theme.spacing(1.25),
        padding: theme.spacing(1.25, 1.5),
        backgroundColor: 'rgba(0,0,0,0.02)',
        border: '1px solid rgba(0,0,0,0.06)',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
            borderColor: theme.palette.primary.main,
            transform: 'translateX(2px)',
        },
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: theme.spacing(4),
    },
    emptyIcon: {
        fontSize: 64,
        color: '#CBD5E0',
        marginBottom: theme.spacing(2),
    },
    attachmentItem: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        cursor: 'pointer',
        border: '1px solid #ECEFF1',
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        backgroundColor: '#FAFAFA',
        '&:hover': {
            backgroundColor: '#F5F5F5',
            borderColor: theme.palette.primary.main,
            transform: 'translateX(3px)',
        },
    },
    attachmentIcon: {
        marginRight: theme.spacing(1.5),
        color: theme.palette.primary.main,
        fontSize: '1.5rem',
    },
    attachmentInfo: {
        flex: 1,
    },
    panelFooter: {
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
        backgroundColor: '#FAFAFA',
    },
    tabsContainer: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginTop: theme.spacing(2),
    },
    tabPanel: {
        padding: theme.spacing(2, 0),
    }
}))

function LLMDocumentDetailDialog({ open, onClose, item, intl, setMessage }) {
    const classes = useStyles()
    const [loading, setLoading] = useState(false)
    const [currentTheme, setCurrentTheme] = useState('green')
    const [statusHistory, setStatusHistory] = useState([])
    const [currentTab, setCurrentTab] = useState(0)
    const [page, setPage] = useState(1)
    const [totalLlmDocumentStatusHistory, setTotalLlmDocumentStatusHistory] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [pageStart, setPageStart] = useState(0)
    const [pageEnd, setPageEnd] = useState(10)

    // Detecta tema atual
    useEffect(() => {
        const detected = detectTheme()
        setCurrentTheme(detected)

        const observer = new MutationObserver(() => {
            const newTheme = detectTheme()
            if (newTheme !== currentTheme) {
                setCurrentTheme(newTheme)
            }
        })

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['style']
        })

        return () => observer.disconnect()
    }, [currentTheme])

    const themeColors = getThemeColors(currentTheme)

    const getFile = async () => {
        setLoading(true)

        let url = await getFileByPath(item.hirerid, item.bucketkey, 2)
        if (url && url.data && url.data.data && url.data.success) {
            setLoading(false)
            window.open(`${url.data.data}`, '_blank')
        } else {
            setMessage(true, 'error', intl.formatMessage({ id: "file.not.found" }))
            setLoading(false)
        }
    }

    const getStatusHistory = async (page = 1) => {
        setLoading(true)
        try {
            const response = await getPaginateByLlmDocumentId(item.id, page)

            if (response && response.success && response.data) {
                setStatusHistory(response.data)
                setTotalLlmDocumentStatusHistory(response.count)
                setPagination(response.count, page)
            } else {
                let message = "Não foi possível obter o histórico de status"
                setMessage(true, 'error', message)
            }
        } catch (err) {
            let message = "Não foi possível obter o histórico de status"
            setMessage(true, 'error', message)
        }
        setLoading(false)
    }

    const setPagination = (count, page) => {
        setTotalPages(Math.ceil(count / 10))
        const start = count > 0 ? (page - 1) * 10 + 1 : 0
        setPageStart(start)
        setPageEnd(Math.min(start + 9, count))
    }

    const onChangePaginate = async (event, value) => {
        setPage(value)
        await getStatusHistory(value)
    }

    useEffect(() => {
        if (item) {
            setCurrentTab(0)
        }
    }, [item])

    const formatDate = (date) => {
        PapiroConsole.log("Formatting date:")
        PapiroConsole.log(date)
        return moment(date).format('DD/MM/YYYY HH:mm')
    }

    const handleTabChange = async (event, newValue) => {
        if (newValue === 1) {
            await getStatusHistory()
        }
        setCurrentTab(newValue);
    }

    const renderDetails = () => {
        if (!item) return null

        return (
            <Grid container spacing={2}>
                {/* Título (somente chamado) */}
                {
                    <Grid item xs={12}>
                        <Typography className={classes.label} variant="subtitle2">
                            <FormattedMessage id="title" defaultMessage="Título" />
                        </Typography>
                        <Typography className={classes.value} variant="body2">
                            {item.title || '-'}
                        </Typography>
                    </Grid>
                }

                <Grid item xs={12}>
                    <Divider />
                </Grid>

                {/* Datas e Tempo de leitura */}
                <Grid item xs={12} md={6}>
                    <Typography className={classes.label} variant="subtitle2">
                        <FormattedMessage id="created.at" defaultMessage="Criado em" />
                    </Typography>
                    <Typography className={classes.value} variant="body2">
                        {item.createdat && formatDate(item.createdat)}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Divider sx={{ mt: 2 }} />
                </Grid>

                {/* Mensagem de erro se houver */}
                {item.processingerror && (
                    <Grid item xs={12} md={6}>
                        <Typography className={classes.label} variant="subtitle2">
                            <FormattedMessage id="error" defaultMessage="Erro" />
                        </Typography>
                        <Typography className={classes.value} variant="body2">
                            {item.processingerror}
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Box className={classes.tabsContainer}>
                        <Tabs value={currentTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tab
                                icon={<AttachFileIcon />}
                                iconPosition="start"
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <FormattedMessage id="attachments" defaultMessage="Anexos" />
                                    </Box>
                                }
                                sx={{ textTransform: 'none', minHeight: 48 }}
                            />
                            <Tab
                                icon={<Timeline />}
                                iconPosition="start"
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <FormattedMessage id="timeline" defaultMessage="Linha do Tempo" />
                                    </Box>
                                }
                                sx={{ textTransform: 'none', minHeight: 48 }}
                            />
                        </Tabs>
                    </Box>

                    {/* Tab Panel - Anexos */}
                    {
                        currentTab === 0 && (
                            <Box className={classes.tabPanel}>
                                {item.bucketkey.length > 0 ? (
                                    <Grid container spacing={1}>
                                        <Paper
                                            className={classes.attachmentItem}
                                            elevation={0}
                                            onClick={() => getFile()}
                                        >
                                            <AttachFileIcon className={classes.attachmentIcon} />
                                            <Box className={classes.attachmentInfo}>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {item.displayTitle || item.title || 'Anexo'}
                                                </Typography>
                                                {/* {attachment.size && (
                                                <Typography variant="caption" color="textSecondary">
                                                    {formatFileSize(attachment.size)}
                                                </Typography>
                                            )} */}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ) : (
                                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                                        <Typography variant="body2" color="textSecondary">
                                            <FormattedMessage id="no.attachments" defaultMessage="Nenhum anexo disponível" />
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )
                    }

                    {/* Tab Panel - Linha do tempo */}
                    {
                        currentTab === 1 && (
                            <>
                                <LLMDocumentStatusHistoryTimeline history={statusHistory} />
                                <Grid container style={{ marginBottom: 12, marginTop: 8 }}>
                                    <Grid item xs={12} md={3}>
                                        <Pagination count={totalPages} page={page} onChange={onChangePaginate} />
                                    </Grid>
                                    <Grid item xs={12} sm={3} style={{ marginTop: 6 }}>
                                        {pageStart} {<FormattedMessage id="to" />} {pageEnd} {<FormattedMessage id="of" />} {totalLlmDocumentStatusHistory} {<FormattedMessage id="register" />}
                                    </Grid>
                                </Grid>
                            </>
                        )
                    }
                </Grid>
            </Grid>
        )
    }

    const renderMainContent = () => (
        <Box className={classes.panelContent}>
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <CircularProgress />
                </Box>
            ) : !item ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <Typography variant="body2" color="textSecondary">
                        <FormattedMessage id="no.data" defaultMessage="Nenhum dado disponível" />
                    </Typography>
                </Box>
            ) : (
                renderDetails()
            )}
        </Box>
    )

    if (open !== undefined) {
        return (
            <Dialog
                fullWidth
                maxWidth="lg"
                open={open}
                onClose={onClose}
                aria-labelledby="details-modal-title"
            >
                <DialogTitle id="details-modal-title" style={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', paddingBottom: 12 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="h6" style={{ fontWeight: 600 }}>
                                <FormattedMessage id="llm.document.details" defaultMessage="Detalhes do Documento" />
                            </Typography>
                            {item && (
                                <Chip
                                    label={`#${item.id || ''}`}
                                    color="primary"
                                    size="small"
                                    style={{ fontWeight: 600 }}
                                />
                            )}
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                    <div style={{ overflowY: 'auto', maxHeight: 'calc(80vh - 140px)', padding: 0 }}>
                        {renderMainContent()}
                    </div>
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #e0e0e0', px: 3, py: 1.5 }}>
                    <Button onClick={onClose} variant="contained" color="primary">
                        <FormattedMessage id="close" defaultMessage="Fechar" />
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    return (
        <div style={{ overflowY: 'auto', height: '100%' }}>
            {renderMainContent()}
        </div>
    )
}

export default injectIntl(LLMDocumentDetailDialog)
